// The pending-sale queue: sales that have been paid for and printed, and are
// waiting to reach the server.
//
// This is the only place in the browser holding something that cannot be
// re-derived. A cached product is a convenience; a queued sale is money that has
// already changed hands, so every operation here errs towards keeping the record.
//
// Namespaced per (store, POS user), unlike the catalog. The reason is not privacy:
// the server attributes a sale to whoever's session pushes it, so a sale queued by
// one cashier and drained under another's login would be filed against the wrong
// person — and cashier attribution is what the shift report is built on.

import type { CheckoutInput, SaleDetail } from '../../shared/types'
import { kvGet, kvPut, queueAll, queueDelete, queuePut } from './idb'
import { withLock } from './lock'
import type { StockDelta } from './merge'

export type QueueState = 'pending' | 'failed'

export interface PendingSale {
  /** The client-generated sale id. Also the server's idempotency key. */
  id: string
  /** `${storeId}:${posUserId}` — who is allowed to push this. */
  ns: string
  /** Exactly what to send. Replayed verbatim, so it must be complete. */
  input: CheckoutInput
  /** What the cashier was shown and what got printed. */
  sale: SaleDetail
  /** Stock this sale took, for the cache overlay until the server confirms it. */
  deltas: StockDelta[]
  queuedAt: string
  attempts: number
  state: QueueState
  lastError: string | null
}

export function namespaceOf(storeId: string, posUserId: string): string {
  return `${storeId}:${posUserId}`
}

// ---------- retry policy (pure) ----------

/** After this many genuine server rejections a sale stops retrying and asks for help. */
export const MAX_ATTEMPTS = 5

/**
 * Errors the server will give the same answer to however many times we ask. A cart
 * naming a product that no longer exists is not going to start existing.
 */
const DETERMINISTIC = new Set([
  'BAD_REQUEST',
  'NOT_FOUND',
  'FORBIDDEN',
  'OVERRIDE_REQUIRES_SUPERVISOR'
])

/**
 * Errors that say nothing about the sale — the network is down, or this browser's
 * session has lapsed. They must NOT count as attempts: a shop offline for an hour
 * would otherwise burn through the attempt budget and mark perfectly good sales
 * failed for the crime of being offline, which is the situation this whole feature
 * exists to handle.
 */
const NOT_THE_SALES_FAULT = new Set(['NETWORK', 'SESSION_EXPIRED'])

export type RetryVerdict = { state: QueueState; attempts: number }

export function verdictFor(current: PendingSale, code: string): RetryVerdict {
  if (NOT_THE_SALES_FAULT.has(code)) return { state: 'pending', attempts: current.attempts }
  if (DETERMINISTIC.has(code)) return { state: 'failed', attempts: current.attempts + 1 }
  const attempts = current.attempts + 1
  return { state: attempts >= MAX_ATTEMPTS ? 'failed' : 'pending', attempts }
}

/** Exponential backoff, capped. Reset on any success. */
export function backoffMs(consecutiveFailures: number): number {
  return Math.min(1000 * 2 ** Math.max(0, consecutiveFailures - 1), 60_000)
}

// ---------- durable state ----------

/**
 * Mirror of the queue, so reading it is synchronous.
 *
 * That matters in two places on the hot path: Charge has to decide instantly, and
 * every catalog read applies the pending-stock overlay. Neither can await a database.
 */
let mirror: PendingSale[] = []
let loaded = false
const listeners = new Set<() => void>()

function notify(): void {
  for (const cb of listeners) {
    try {
      cb()
    } catch (err) {
      console.warn('[web] queue listener threw', err)
    }
  }
}

export function subscribe(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/**
 * Oldest first, and deterministic. IndexedDB hands rows back in key order, and the
 * key is a random UUID — so without this a reload would replay a shop's sales in
 * arbitrary order, which reads as nonsense in the sales list and hands out receipt
 * numbers that go backwards.
 */
function inOrder(a: PendingSale, b: PendingSale): number {
  return a.queuedAt.localeCompare(b.queuedAt) || a.id.localeCompare(b.id)
}

export async function load(): Promise<void> {
  if (loaded) return
  mirror = (await queueAll<PendingSale>()).sort(inOrder)
  loaded = true
  notify()
}

/** Everything queued for one namespace, oldest first. */
export function list(ns: string): PendingSale[] {
  return mirror.filter((s) => s.ns === ns)
}

/** The next sale to push: oldest, still pending. */
export function nextPending(ns: string): PendingSale | undefined {
  return mirror.find((s) => s.ns === ns && s.state === 'pending')
}

export function counts(ns: string): { pending: number; failed: number } {
  let pending = 0
  let failed = 0
  for (const s of mirror) {
    if (s.ns !== ns) continue
    if (s.state === 'pending') pending++
    else failed++
  }
  return { pending, failed }
}

/**
 * Stock taken by sales the server has not confirmed, for the catalog overlay.
 *
 * Includes FAILED sales as well as pending ones, deliberately: a failed sale still
 * physically left the shop. Its stock is gone whether or not the cloud ever accepts
 * the record, so continuing to subtract it is the honest answer until a human
 * decides what to do.
 */
export function pendingDeltas(ns: string): StockDelta[] {
  return mirror.filter((s) => s.ns === ns).flatMap((s) => s.deltas)
}

export async function enqueue(sale: PendingSale): Promise<void> {
  // Durable BEFORE visible. A sale showing as pending that did not survive the
  // write is worse than one that briefly does not show at all.
  await queuePut(sale)
  // Sorted rather than appended, so the live order matches the order a reload
  // would produce. In practice this always appends — queuedAt is stamped here.
  mirror = [...mirror, sale].sort(inOrder)
  notify()
}

/** The sale reached the server. Drop it — the cloud is the record now. */
export async function confirm(id: string): Promise<void> {
  await queueDelete(id)
  mirror = mirror.filter((s) => s.id !== id)
  notify()
}

export async function recordFailure(id: string, code: string, message: string): Promise<void> {
  const current = mirror.find((s) => s.id === id)
  if (!current) return
  const { state, attempts } = verdictFor(current, code)
  const next: PendingSale = { ...current, state, attempts, lastError: message }
  await queuePut(next)
  mirror = mirror.map((s) => (s.id === id ? next : s))
  notify()
}

/** Put a failed sale back in the queue — the cashier asking us to try again. */
export async function retry(id: string): Promise<void> {
  const current = mirror.find((s) => s.id === id)
  if (!current) return
  const next: PendingSale = { ...current, state: 'pending', attempts: 0, lastError: null }
  await queuePut(next)
  mirror = mirror.map((s) => (s.id === id ? next : s))
  notify()
}

// ---------- receipt numbers ----------

/**
 * Receipt numbers claimed from the server ahead of time, so Charge never waits to
 * learn what to print. Refilled in the background well before they run out.
 *
 * THE STORED BLOCK IS THE AUTHORITY, NEVER THE IN-MEMORY COPY. Two tabs of the same
 * shop share one IndexedDB, but not one heap: an earlier version kept the block in
 * a module-level array per tab, and both tabs happily handed out WEB-0057 to
 * different customers. The server catches that (a taken receipt number is re-minted
 * and the sale flagged), but the paper in someone's hand is then wrong, which is
 * not something to do routinely.
 *
 * So every take is a read-modify-write against storage, under a cross-tab lock.
 * The map below is only a cache for receiptsLeft(), which decides when to top up
 * and can be stale by a few without consequence.
 */
interface ReceiptBlock {
  numbers: string[]
}

const blocks = new Map<string, string[]>()

function blockKey(ns: string): string {
  return `rcpt:${ns}`
}

function lockName(ns: string): string {
  return `tendally-receipts-${ns}`
}

export async function loadReceipts(ns: string): Promise<void> {
  const stored = await kvGet<ReceiptBlock>(blockKey(ns))
  blocks.set(ns, stored?.numbers ?? [])
}

export function receiptsLeft(ns: string): number {
  return blocks.get(ns)?.length ?? 0
}

/** Take the next number. Undefined when the block is empty — the caller must wait. */
export async function takeReceipt(ns: string): Promise<string | undefined> {
  return withLock(lockName(ns), 'wait', async () => {
    const stored = await kvGet<ReceiptBlock>(blockKey(ns))
    const numbers = stored?.numbers ?? []
    if (numbers.length === 0) {
      blocks.set(ns, [])
      return undefined
    }
    const [head, ...rest] = numbers
    // Persisted BEFORE the number is handed back, so a crash between taking and
    // selling burns a receipt number rather than risking two sales printing one.
    await kvPut(blockKey(ns), { numbers: rest })
    blocks.set(ns, rest)
    return head
  })
}

export async function addReceipts(ns: string, numbers: string[]): Promise<void> {
  await withLock(lockName(ns), 'wait', async () => {
    // Re-read rather than append to the local copy: another tab may have spent
    // several numbers since this one last looked.
    const stored = await kvGet<ReceiptBlock>(blockKey(ns))
    const merged = [...(stored?.numbers ?? []), ...numbers]
    await kvPut(blockKey(ns), { numbers: merged })
    blocks.set(ns, merged)
  })
}

/** Test seam. */
export function _reset(): void {
  mirror = []
  loaded = false
  blocks.clear()
  listeners.clear()
}
